import { getConfig } from '@local/app-config';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/GridLegacy';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { decodeToken } from 'react-jwt';
import { useNavigate } from 'react-router';

import {
    useCentralSeequentLicenseTokenMutation,
    useCentralBentleyLicenseTokenMutation,
} from '../../../apiClients/authenticatedIdentityClient/requests/CentralLicenseTokenApi';
import { useLoadEvoCentralInstancesQuery } from '../../../apiClients/discoveryClient/requests/DiscoveryApi';
import type { CentralInstance } from '../../../apiClients/entitlementsClient/requests/Entitlements.types';
import { useLoadCentralInstancesQuery } from '../../../apiClients/entitlementsClient/requests/EntitlementsAPI';
import type {
    AccessAndIDTokenWithExpiresAt as AccessToken,
    IdTokenDecoded,
} from '../../../apiClients/types';
import { LOGIN_ERROR_ROUTE, LOGIN_ROUTE, UUID_REGEX } from '../../../constants';
import {
    getCombinedToken as getAccessToken,
    isLoggedInWithBentleyToken,
} from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { getDeeplink } from '../../../store/sessionStorageHelpers/deeplinkHelper/deeplinkHelper';
import { setLicenseToken } from '../../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { setSelectedInstanceState } from '../../../store/sessionStorageHelpers/selectedInstanceHelper';
import { Background } from '../../background/Background';
import { CentralBackground } from '../../background/centralBackground/CentralBackground';
import { OrgHubSelectMenu } from '../OrgHubSelectMenu/OrgHubSelectMenu';
import { useStyles } from '../sharedSelector.styles';
import {
    getAllInstances,
    getPreviousOrDefaultInstance,
    optionsWithEvo,
    optionGroupsWithEvo,
} from '../utils';

export function CentralOrgSelector({ homePagePath = '/' }: { homePagePath?: string }) {
    const isLoggedInWithBentley = isLoggedInWithBentleyToken();
    const { isLoading: entitlementsLoading, data, error } = useLoadCentralInstancesQuery();
    const {
        isLoading: discoveryLoading,
        data: evoData,
        error: evoError,
    } = useLoadEvoCentralInstancesQuery(undefined, {
        skip: !isLoggedInWithBentley,
    });
    const navigate = useNavigate();
    const { classes } = useStyles();
    const config = getConfig();

    // Instances the user can reach, whether they come from entitlements, discovery, or both.
    const allInstances = getAllInstances(data?.servers, evoData?.instances);

    const [selectedInstance, setSelectedInstance] = useState<string | null>(null);
    const [userName, setUserName] = useState<string | null>();
    const [correctIdentityProvider, setCorrectIdentityProvider] = useState(true);
    const [featureLoading, setFeatureLoading] = useState(false);

    const [getCentralSeequentLicenseToken] = useCentralSeequentLicenseTokenMutation();
    const [getCentralBentleyLicenseToken] = useCentralBentleyLicenseTokenMutation();

    useEffect(() => {
        if (error || evoError) {
            // If Central Portal can't retrieve the list of central instances, return the user to the login page.
            navigate(`${LOGIN_ERROR_ROUTE}?error=entitlements_failure`);
            return;
        }
        // Both sources contribute instances, so wait for them to settle before acting on the list.
        if (entitlementsLoading || discoveryLoading) {
            return;
        }
        if (data && allInstances.length === 0) {
            // If the user is not in any central instances, return the user to the login page.
            navigate(`${LOGIN_ERROR_ROUTE}?error=no_available_instances`);
            return;
        }

        const checkCanBypassOrgSelector = async () => {
            // If the user is only a member of a single instance, we can skip the org selector.
            if (allInstances.length === 1) {
                const instance = allInstances[0];
                return navigateToInstance(
                    instance.host,
                    instance.entitlementId,
                    instance.instanceUUID,
                );
            }

            // If the deeplink destination includes an instanceUuid, we can skip the org selector.
            const destination = getDeeplink(homePagePath);
            const result = destination.match(UUID_REGEX);
            if (result && result[0]) {
                const instance = allInstances.find((server) => server.instanceUUID === result[0]);
                if (instance) {
                    return navigateToInstance(
                        instance.host,
                        instance.entitlementId,
                        instance.instanceUUID,
                    );
                }
            }
            return undefined;
        };

        checkCanBypassOrgSelector();
    }, [error, data, evoData, entitlementsLoading, discoveryLoading]);

    useEffect(() => {
        const accessToken = getAccessToken();
        const decodedToken = getDecodedIDToken(accessToken);
        if (decodedToken) {
            setUserName(`${decodedToken?.given_name} ${decodedToken?.family_name}`);
        }
        const expired = accessToken && accessToken.expires_at <= Date.now();
        if (expired) {
            // Scenario where user sits on org selector page for 24 hours
            navigate(`${LOGIN_ROUTE}`);
        }
    }, []);

    function getDecodedIDToken(accessToken: AccessToken | null) {
        if (accessToken && accessToken.id_token) {
            return decodeToken<IdTokenDecoded>(accessToken.id_token);
        }
        return null;
    }

    if (entitlementsLoading || discoveryLoading || allInstances.length === 1) {
        const Spinner = (
            <Grid
                item
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100px',
                }}
                alignContent="center"
                justifyContent="center"
                display="grid"
            >
                <CircularProgress automation-id="spinner-loading" />
            </Grid>
        );
        return config.bentleyIdEnabledCentral ? (
            <CentralBackground>{Spinner}</CentralBackground>
        ) : (
            <Background>{Spinner}</Background>
        );
    }

    async function featureCheck(filteredInstance: CentralInstance) {
        const response = await fetch(`https://${filteredInstance.host}/central/feature`);
        if (!response.ok) {
            throw new Error(`Failed to fetch feature data from: ${filteredInstance.host}`);
        }
        const featureData = await response.json();
        setFeatureLoading(false);
        if (
            (featureData.identity_provider === 'Bentley' && isLoggedInWithBentley) ||
            (featureData.identity_provider === 'Seequent' && !isLoggedInWithBentley)
        ) {
            setCorrectIdentityProvider(true);
        } else {
            setCorrectIdentityProvider(false);
        }
    }

    async function selectInstance(): Promise<void> {
        const filteredInstance = allInstances.find(
            (server) => server.instanceUUID === selectedInstance,
        );
        if (!filteredInstance || !correctIdentityProvider) {
            return undefined;
        }
        const { host, entitlementId, instanceUUID } = filteredInstance;
        return navigateToInstance(host, entitlementId, instanceUUID);
    }

    async function navigateToInstance(host: string, entitlementId: string, instanceUUID: string) {
        setSelectedInstanceState(host);

        try {
            if (isLoggedInWithBentley && !entitlementId) {
                const accessToken = getAccessToken()?.access_token;
                if (!accessToken) {
                    navigate(`${LOGIN_ERROR_ROUTE}?error=instance_unavailable`);
                    return;
                }
                // Discovery-only instances are authorized by the IMS token and have no IDP entitlement to exchange.
                setLicenseToken(accessToken);
            } else if (isLoggedInWithBentley) {
                await getCentralBentleyLicenseToken(entitlementId).unwrap();
            } else {
                await getCentralSeequentLicenseToken(entitlementId).unwrap();
            }
        } catch (_tokenError) {
            navigate(`${LOGIN_ERROR_ROUTE}?error=instance_unavailable`);
            return;
        }
        const destination = getDeeplink(homePagePath);
        if (destination.includes(instanceUUID)) {
            return navigate(destination);
        }
        return navigate(`/${instanceUUID}${destination}`);
    }

    async function selectInstanceWithValidation(instanceUUID?: string) {
        const filteredInstance = allInstances.find(
            (server) => server.instanceUUID === (instanceUUID ?? selectedInstance),
        );
        if (!filteredInstance) {
            return;
        }
        setFeatureLoading(true);
        await featureCheck(filteredInstance);
    }

    function handleOrgSelect(key: string) {
        setSelectedInstance(key);
        selectInstanceWithValidation(key);
    }

    return config.bentleyIdEnabledCentral ? (
        <CentralBackground correctIdentityProvider={correctIdentityProvider}>
            <Grid item xs={12} className={classes.formContainer}>
                <Grid className={classes.label}>
                    <Typography>Name: {userName || null}</Typography>
                    <Grid item xs className={classes.selectMenu}>
                        <OrgHubSelectMenu
                            options={optionsWithEvo(data, evoData)}
                            optionGroups={optionGroupsWithEvo(data, evoData)}
                            onSelected={(key) => handleOrgSelect(key)}
                            defaultValue={getPreviousOrDefaultInstance(data, evoData)}
                            width={360}
                        />
                    </Grid>
                </Grid>
                <Button
                    color="primary"
                    variant="contained"
                    size="large"
                    className={classes.button}
                    onClick={() => selectInstance()}
                    disabled={featureLoading || !selectedInstance || !correctIdentityProvider}
                >
                    {featureLoading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        'Continue to Central'
                    )}
                </Button>
            </Grid>
        </CentralBackground>
    ) : (
        <Background>
            <Grid item xs={12} className={classes.formContainer}>
                <Grid className={classes.label}>
                    <Typography>Name: {userName || null}</Typography>
                    <Grid item xs className={classes.selectMenu}>
                        <OrgHubSelectMenu
                            options={optionsWithEvo(data, evoData)}
                            optionGroups={optionGroupsWithEvo(data, evoData)}
                            onSelected={(key) => setSelectedInstance(key)}
                            defaultValue={getPreviousOrDefaultInstance(data, evoData)}
                            width={360}
                        />
                    </Grid>
                </Grid>
                <Button
                    color="primary"
                    variant="contained"
                    size="large"
                    className={classes.button}
                    onClick={() => selectInstance()}
                    disabled={selectedInstance === undefined}
                >
                    Continue to Central
                </Button>
            </Grid>
        </Background>
    );
}
